import { useCallback, useState } from "react";

/** State persisted to localStorage. Merges stored object values over defaults. */
export function useLocalStorage<T>(key: string, initial: T): [T, (value: T | ((prev: T) => T)) => void] {
  const [value, setValue] = useState<T>(() => {
    if (typeof window === "undefined") return initial;
    try {
      const raw = window.localStorage.getItem(key);
      if (raw == null) return initial;
      const parsed = JSON.parse(raw);
      // Merge so newly-added object keys fall back to defaults.
      return initial && typeof initial === "object" && !Array.isArray(initial)
        ? ({ ...(initial as object), ...(parsed as object) } as T)
        : (parsed as T);
    } catch {
      return initial;
    }
  });

  const set = useCallback(
    (next: T | ((prev: T) => T)) => {
      setValue((prev) => {
        const resolved = typeof next === "function" ? (next as (p: T) => T)(prev) : next;
        try {
          window.localStorage.setItem(key, JSON.stringify(resolved));
        } catch {
          /* ignore quota/availability errors */
        }
        return resolved;
      });
    },
    [key],
  );

  return [value, set];
}
